import { ErrorResponse, type User } from 'oidc-client-ts';
import { useEffect, useRef } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { currentPathWithoutAuthParams } from '../utils/oidc.ts';
import { reportEvent } from '../utils/telemetry.ts';

const CALLBACK_RETRY_KEY = 'medcom.signin-callback-retried';

/**
 * Why did signinCallback reject? "No matching state found in storage" means the URL carried a
 * state for which oidc-client-ts found no entry, and the two ways to get there look identical
 * from the outside: the URL held a stale state (see stripAuthParams / LC-980), or the store
 * lost the entry that was written before the redirect.
 *
 * Distinguishing them needs the state ids on both sides. Ids only — a state entry's value holds
 * the PKCE code_verifier, and the user entry holds tokens. Neither may leave the browser.
 */
const describeSigninFailure = (error: Error): Record<string, string> => {
  const urlStates = new URL(window.location.href).searchParams.getAll('state');
  let storedStateIds: string[] = [];
  let storageReadable = true;
  try {
    storedStateIds = Object.keys(window.localStorage)
      .filter(key => key.startsWith('oidc.') && !key.includes('user:'))
      .map(key => key.slice('oidc.'.length));
  } catch {
    // Private mode, or a profile whose storage failed to open — itself a candidate cause.
    storageReadable = false;
  }
  return {
    error: error.message,
    // More than one is the LC-980 signature: a stale state echoed back on top of the fresh one,
    // of which oidc-client-ts reads the first.
    urlStateCount: String(urlStates.length),
    urlStates: urlStates.join(','),
    storedStateCount: String(storedStateIds.length),
    storedStates: storedStateIds.join(','),
    // True means the URL's state simply is not the one we stored; false with entries present
    // means we stored something else; zero entries points at storage, not at the URL.
    anyUrlStateStored: String(urlStates.some(state => storedStateIds.includes(state))),
    storageReadable: String(storageReadable),
  };
};

type AuthEventsOptions = {
  autoSignin?: boolean;
  /** Where a user whose session has ended goes instead of the login form; without a path the page reloads. */
  sessionEndedPath?: (user: User | null | undefined) => string | undefined;
};

export const useAuthEvents = ({ autoSignin = true, sessionEndedPath }: AuthEventsOptions = {}) => {
  const auth = useAuth();
  const hasTriedSignin = useRef(false);

  // automatically sign-in
  useEffect(() => {
    if (autoSignin && !hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin.current) {
      auth.signinRedirect();
      hasTriedSignin.current = true;
    }
  }, [auth, autoSignin]);

  // A failed signin callback leaves the auth params in the URL, so neither the lib
  // (it only initialises once) nor the auto sign-in above retries — the user is stuck
  // on the error screen until a manual reload. Clean the URL and sign in once more.
  useEffect(() => {
    if (!auth.error || !hasAuthParams() || window.sessionStorage.getItem(CALLBACK_RETRY_KEY)) {
      return;
    }
    window.sessionStorage.setItem(CALLBACK_RETRY_KEY, '1');

    // Record why the callback failed before the evidence is destroyed: the line below strips
    // the params, and the state entry is gone from storage either way. Telemetry shows the
    // failure recovers within a second, but not what was missing.
    reportEvent('signin-callback-failed', describeSigninFailure(auth.error));

    window.history.replaceState({}, document.title, currentPathWithoutAuthParams());
    // Same flag the auto sign-in above sets. Without it, stripping the params flips
    // hasAuthParams() to false and reopens that effect's guard, so it can fire a second
    // signinRedirect before activeNavigator (a React state update, not synchronous) is set.
    hasTriedSignin.current = true;
    auth.signinRedirect();
  }, [auth]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      window.sessionStorage.removeItem(CALLBACK_RETRY_KEY);
    }
  }, [auth.isAuthenticated]);

  // handle renew errors, e.g. session timeouts
  useEffect(() => {
    return auth.events.addSilentRenewError(async error => {
      // retry on timeout is handled by the lib
      // see https://github.com/authts/oidc-client-ts/blob/main/src/SilentRenewService.ts#L54C11-L54C11
      // Keycloak answers invalid_grant once the session is gone; anything else may recover on a reload
      const sessionEnded = error instanceof ErrorResponse && error.error === 'invalid_grant';
      reportEvent('silent-renew-failed', {
        error: (error instanceof ErrorResponse && error.error) || error.message,
        sessionEnded: String(sessionEnded),
      });
      const path = sessionEnded ? sessionEndedPath?.(auth.user) : undefined;
      // removeUser signs the page out before it navigates away; the auto sign-in must not race that
      hasTriedSignin.current = true;
      await auth.removeUser();
      if (path) {
        window.location.assign(path);
      } else {
        window.location.reload();
      }
    });
  }, [auth, sessionEndedPath]);

  // multi-tab logout (enable via monitorSession: true)
  // useEffect(() => {
  //   return auth.events.addUserSignedOut(async () => {
  //     await auth.removeUser();
  //     window.location.reload();
  //   });
  // }, [auth]);
};
